import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useBlockHighlight } from '@ContractBuilder/utils/use-block-highlight';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import type { AuditEntry } from '@domain/types/audit-log';
import { InfiniteLoader } from '@features/infinite-loader';
import { XIcon } from '@heroicons/react/outline';
import { useAllTeams } from '@queries/fetchTeams';

import { deriveUsers } from '../utils/derive-users';
import { groupByDay } from '../utils/group-by-day';
import { mapAuditItemUser } from '../utils/map-audit-item-user';

import type { ActivitySort } from './ActivityControls';
import { ActivityControls, EVERYONE } from './ActivityControls';
import { ActivityEntry } from './ActivityEntry';

interface ActivityProps {
  blocks: ResourceBlock[];
  sections: { id: string; label: string }[];
  data: AuditEntry[];
  onClose: () => void;
  filterKey: string;
  onFilter: (key: string) => void;
  sort: ActivitySort;
  onToggleSort: () => void;
}

const PAGE_SIZE = 30;
const userKey = (entry: AuditEntry) => entry.user?.id || entry.user?.name;

// A flat row stream (day headings interleaved with entries) so the existing InfiniteLoader can window it.
type Row = { id: string; kind: 'heading'; label: string } | { id: string; kind: 'entry'; entry: AuditEntry };

export const Activity: FC<ActivityProps> = ({
  blocks,
  sections,
  data,
  onClose,
  filterKey,
  onFilter,
  sort,
  onToggleSort,
}) => {
  const { data: teams = [] } = useAllTeams();
  const { onClick: onJumpToTarget } = useBlockHighlight();
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Reset the window to the top when the view of the log changes (not on live refetch).
  useEffect(() => setLimit(PAGE_SIZE), [filterKey, sort]);

  const mapped = data.map(mapAuditItemUser);
  const users = deriveUsers(mapped);

  const filtered = filterKey === EVERYONE ? mapped : mapped.filter((entry) => userKey(entry) === filterKey);
  const ordered = sort === 'oldest' ? [...filtered].reverse() : filtered;

  const rows: Row[] = groupByDay(ordered, new Date()).flatMap((group) => [
    { id: `heading-${group.label}`, kind: 'heading', label: group.label },
    ...group.entries.map((entry): Row => ({ id: entry.id, kind: 'entry', entry })),
  ]);

  const renderRow = (row: Row): ReactNode =>
    row.kind === 'heading' ? (
      <div className="border-info-200 bg-info-50 text-info-500 border-y py-1.5 pr-3 pl-7.5 text-xs font-semibold uppercase">
        {row.label}
      </div>
    ) : (
      <ActivityEntry
        blocks={blocks}
        sections={sections}
        entry={row.entry}
        teams={teams}
        onJumpToTarget={onJumpToTarget}
      />
    );

  return (
    <div className="flex w-full flex-col bg-white">
      <div className="border-info-200 flex items-center justify-between border-b px-7.5 py-[15px]">
        <h2 className="text-base leading-6 font-normal text-black">Activity</h2>
        <XIcon className="text-info-900 hover:text-info-600 size-4.5 cursor-pointer" onClick={onClose} />
      </div>
      <ActivityControls
        users={users}
        filterKey={filterKey}
        onFilter={onFilter}
        sort={sort}
        onToggleSort={onToggleSort}
      />
      <div className="overflow-x-hidden overflow-y-auto">
        <InfiniteLoader
          data={rows.slice(0, limit)}
          total={rows.length}
          renderItem={renderRow}
          onLoadMoreItems={() => setLimit((current) => current + PAGE_SIZE)}
        />
      </div>
    </div>
  );
};
